import React, { useEffect, useState } from "react";
import { X, Copy, Check } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { useStore } from "@/context/StoreContext";

// A quiet, one-time nudge for overseas visitors: confirms prices are shown in
// their currency and — when the owner has set one — offers a welcome code.
export const CurrencyHint = () => {
  const { currency, isConverted, currencies } = useCurrency();
  const { settings, bagOpen, searchOpen } = useStore();
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const [consented, setConsented] = useState(() => { try { return !!JSON.parse(localStorage.getItem("nba_consent")); } catch { return false; } });

  useEffect(() => {
    const h = () => setConsented(true);
    window.addEventListener("nba-consent", h);
    return () => window.removeEventListener("nba-consent", h);
  }, []);

  const offerOn = !!(settings?.welcome_offer_enabled && settings?.welcome_offer_code);
  const code = (settings?.welcome_offer_code || "").toUpperCase();

  useEffect(() => {
    if (!isConverted || !consented) return;
    try { if (localStorage.getItem("nba_currency_hint_seen")) return; } catch { /* ignore */ }
    const t = setTimeout(() => setShow(true), 1800);
    return () => clearTimeout(t);
  }, [isConverted, consented]);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem("nba_currency_hint_seen", "1"); } catch { /* ignore */ }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };

  if (!show || bagOpen || searchOpen || !consented) return null;

  return (
    <div
      data-testid="currency-hint"
      className="fixed bottom-6 right-6 z-[60] max-w-[20rem] bg-espresso text-cream px-5 py-4 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <button onClick={dismiss} data-testid="currency-hint-dismiss" aria-label="Dismiss" className="absolute top-2.5 right-2.5 text-cream/60 hover:text-cream transition-colors">
        <X size={15} strokeWidth={1.6} />
      </button>
      <p className="font-serif text-lg leading-snug mb-1">Shopping from abroad?</p>
      <p className="text-xs text-cream/75 leading-relaxed pr-3">
        Prices are shown in {currency} ({currencies[currency]}). Change your currency anytime from the header.
      </p>
      {offerOn && (
        <div className="mt-3 pt-3 border-t border-cream/15">
          <p className="text-xs text-cream/80 leading-relaxed mb-2">
            {settings.welcome_offer_text || "A little welcome to your first order."}
          </p>
          <button onClick={copy} data-testid="currency-hint-copy" className="flex items-center gap-2.5 bg-cream/10 hover:bg-cream/20 transition-colors px-3.5 py-2.5 w-full">
            <span className="text-sm tracking-[0.22em] flex-1 text-left">{code}</span>
            {copied ? <Check size={14} strokeWidth={1.8} className="text-sage" /> : <Copy size={14} strokeWidth={1.6} className="text-cream/70" />}
            <span className="text-[0.6rem] tracking-[0.16em] uppercase text-cream/60">{copied ? "Copied" : "Copy"}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CurrencyHint;
